import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import { useDemoIdentity } from "../../lib/DemoIdentityContext";
import { useTenantTerms } from "../../lib/TenantConfigContext";
import { useVisibleNav } from "../Layout";
import { useMsdScope } from "../../lib/msdScope";
import { useRealtimeSync } from "../../lib/useRealtimeSync";
import { brandInitials } from "../../lib/brand";
import { useExitDelay } from "../../lib/useExitDelay";
import { usePinnedSidebar } from "../../lib/usePinnedSidebar";
import { ROLES } from "../../lib/roleViews";
import { Icon } from "./Icon";

// The tenant app shell: a light sidebar that folds to an icon rail, the top
// bar and the bottom toast. Colours come from the .msd tokens in index.css, so
// the dark theme follows without extra code here.

const ToastContext = createContext(() => {});
export const useMsdToast = () => useContext(ToastContext);

const THEME_KEY = "msd-theme";
const PIN_KEY = "msd.sidebar.pinned";

// Nav entries in their groups: label, icon and the app routes behind each one.
// "Organization" covers the regions, dealers and workshops pages.
const NAV = [
  {
    label: "Insights",
    items: [
      { key: "tower", icon: "trend", label: "Workshop analytics", to: "/analytics", pages: ["analytics"] },
      { key: "svc", icon: "rupee", label: "Service analytics", to: "/service-analytics", pages: ["analytics"] },
    ],
  },
  {
    label: "Workspace",
    items: [
      { key: "desk", icon: "head", label: "Advisor's desk", to: "/desk", pages: ["desk"] },
      { key: "history", icon: "chat", label: "Chat history", to: "/chat-history", pages: ["chatHistory"] },
      { key: "book", icon: "plus", label: "Book a service", to: "/create", pages: ["create"] },
      { key: "health", icon: "pulse", label: "System health", to: "/system-health", pages: ["systemHealth"] },
    ],
  },
  {
    label: "Manage",
    items: [
      { key: "org", icon: "map", label: "Organization", to: "/regions", also: ["/dealers", "/workshops"], pages: ["regions", "dealers", "workshops"] },
      { key: "people", icon: "people", label: "People", to: "/people", pages: ["people"] },
      { key: "roles", icon: "shield", label: "Roles and access", to: "/roles", pages: ["roles"] },
    ],
  },
];

const RAIL = 72;
const PANEL = 248;
// One easing for the whole sidebar, so width, labels and shadow move together.
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

function useNavItems() {
  const loc = useLocation();
  const visible = useVisibleNav();
  const allowed = useMemo(() => new Set(visible.map((v) => v.page)), [visible]);
  // Only the pages this login may open; a group left with none is dropped.
  return NAV.map((g) => ({
    label: g.label,
    items: g.items
      .filter((n) => n.pages.some((p) => allowed.has(p)))
      .map((n) => {
        const paths = [n.to, ...(n.also || [])];
        return { ...n, active: paths.some((p) => loc.pathname === p || loc.pathname.startsWith(`${p}/`)) };
      }),
  })).filter((g) => g.items.length);
}

// The sidebar's contents. `open` decides whether labels show; the parent owns
// the width and how it got open (pinned, hovered, or the phone menu).
function SidebarBody({ items, badge, open, pinned, onPin, onGo, onTheme, image, onClose }) {
  const fade = {
    opacity: open ? 1 : 0,
    transform: open ? "translateX(0)" : "translateX(-6px)",
    transition: `opacity ${open ? 220 : 100}ms ${EASE} ${open ? 60 : 0}ms, transform 280ms ${EASE}`,
  };

  const item = (n) => (
    <button
      key={n.key}
      type="button"
      onClick={() => onGo(n.to)}
      aria-label={n.label}
      aria-current={n.active ? "page" : undefined}
      data-testid={`msd-nav-${n.key}`}
      className={`group relative flex h-10 w-full shrink-0 items-center gap-3 whitespace-nowrap rounded-[10px] px-[14px] text-left text-[14px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-msd-royal-line ${
        n.active ? "bg-msd-soft font-semibold text-msd-ink" : "font-medium text-msd-muted hover:bg-msd-soft hover:text-msd-ink"
      }`}
    >
      <span className={`relative flex h-5 w-5 flex-none items-center justify-center ${n.active ? "text-msd-ink" : "text-msd-faint group-hover:text-msd-ink2"}`}>
        <Icon name={n.icon} size={20} />
        {n.key === "desk" && badge ? (
          // Folded, the count shrinks to a dot on the icon.
          <span
            className="absolute -right-[3px] -top-[3px] h-2 w-2 rounded-full bg-msd-brick ring-2 ring-[var(--surface)]"
            style={{ opacity: open ? 0 : 1, transition: `opacity 160ms ${EASE}` }}
          />
        ) : null}
      </span>
      <span className="truncate" style={fade}>
        {n.label}
      </span>
      {n.key === "desk" && badge ? (
        <span className="ml-auto rounded-full bg-msd-brick-soft px-2 py-px text-[12px] font-semibold text-msd-brick-ink" style={fade}>
          {badge}
        </span>
      ) : null}
    </button>
  );

  return (
    <>
      <div className="flex h-16 flex-none items-center gap-2 border-b border-msd-line pl-[18px] pr-3">
        <img src="/chat360-mark.png" alt="Chat360" className="h-9 w-9 flex-none" />
        <img src="/chat360-wordmark.png" alt="" aria-hidden="true" className="msd-wordmark h-[22px] w-auto flex-none" style={fade} />
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="ml-auto flex h-8 w-8 flex-none items-center justify-center rounded-lg text-msd-faint hover:bg-msd-soft hover:text-msd-ink2"
          >
            <Icon name="x" size={18} stroke={2} />
          </button>
        ) : (
          <button
            type="button"
            onClick={onPin}
            aria-label={pinned ? "Fold the sidebar" : "Keep the sidebar open"}
            title={pinned ? "Fold the sidebar" : "Keep the sidebar open"}
            tabIndex={open ? 0 : -1}
            data-testid="msd-pin"
            className="ml-auto flex h-8 w-8 flex-none items-center justify-center rounded-lg text-msd-faint hover:bg-msd-soft hover:text-msd-ink2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-msd-royal-line"
            style={{ ...fade, pointerEvents: open ? "auto" : "none" }}
          >
            <Icon name={pinned ? "pinoff" : "pin"} size={16} stroke={2} />
          </button>
        )}
      </div>

      <nav aria-label="Main" className="scrollbar-none flex min-h-0 grow flex-col overflow-y-auto overflow-x-hidden px-3 pb-3 pt-2">
        {items.map((g, gi) => (
          <div key={g.label} className={`flex flex-col gap-0.5 ${gi ? "mt-5" : ""}`}>
            {/* Folded, a group heading is only a short rule; open, its name. */}
            <div className="relative mb-1 flex h-6 shrink-0 items-center whitespace-nowrap px-[14px]">
              <span
                aria-hidden="true"
                className="absolute left-[22px] top-1/2 h-px w-5 bg-msd-line"
                style={{ opacity: open ? 0 : 1, transition: `opacity 160ms ${EASE}` }}
              />
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-msd-faint" style={fade}>
                {g.label}
              </span>
            </div>
            {g.items.map(item)}
          </div>
        ))}
      </nav>

      {image ? (
        // The brand's own picture (Super Admin > Brands), only while the panel
        // is open: squeezed into the rail it would be an unreadable sliver.
        <div
          className="relative h-[200px] min-h-[80px] shrink overflow-hidden after:pointer-events-none after:absolute after:inset-0 after:bg-[linear-gradient(180deg,var(--surface)_0%,transparent_45%)] after:content-['']"
          style={{ opacity: open ? 1 : 0, transition: `opacity 220ms ${EASE}` }}
        >
          <img src={image} alt="" className="block h-full w-full object-cover" />
        </div>
      ) : null}

      <div className="mt-auto flex-none border-t border-msd-line p-3">
        <AccountMenu open={open} fade={fade} onTheme={onTheme} />
      </div>
    </>
  );
}

function Sidebar({ items, badge, onTheme, image }) {
  const nav = useNavigate();
  // Open by default on wide screens; folded on laptops so the desk has room.
  const { pinned, peek, open, togglePin, closePeek, handlers } = usePinnedSidebar(
    PIN_KEY,
    () => window.innerWidth >= 1440,
  );

  return (
    <div
      className="relative shrink-0 max-[860px]:hidden"
      style={{ width: pinned ? PANEL : RAIL, transition: `width 280ms ${EASE}` }}
    >
      <aside
        {...handlers}
        className="absolute bottom-0 left-0 top-0 z-40 flex flex-col overflow-hidden border-r border-msd-line bg-msd-surface"
        style={{
          width: open ? PANEL : RAIL,
          transition: `width 280ms ${EASE}, box-shadow 280ms ${EASE}`,
          boxShadow: peek && !pinned ? "16px 0 40px -16px rgba(16,24,40,.22)" : "none",
        }}
        data-testid="msd-sidebar"
      >
        <SidebarBody
          items={items}
          badge={badge}
          open={open}
          pinned={pinned}
          onPin={togglePin}
          onGo={(to) => {
            closePeek();
            nav(to);
          }}
          onTheme={onTheme}
          image={image}
        />
      </aside>
    </div>
  );
}

// Phones: the same sidebar, slid in over the page from the menu button.
function Drawer({ items, badge, onClose, onTheme, image, leaving }) {
  const nav = useNavigate();
  return (
    <>
      <div className={`fixed inset-0 z-50 bg-[rgba(16,24,40,.24)] ${leaving ? "msd-scrim-out" : "msd-scrim-in"}`} onClick={onClose} />
      <aside
        aria-label="Menu"
        className={`fixed bottom-[10px] left-[10px] top-[10px] z-[51] flex w-[280px] flex-col overflow-hidden rounded-2xl bg-msd-surface shadow-msd-pop ${
          leaving ? "msd-drawer-out" : "msd-drawer-in"
        }`}
        data-testid="msd-drawer"
      >
        <SidebarBody
          items={items}
          badge={badge}
          open
          onGo={(to) => {
            onClose();
            nav(to);
          }}
          onTheme={onTheme}
          image={image}
          onClose={onClose}
        />
      </aside>
    </>
  );
}

const POP =
  "absolute right-0 z-[60] rounded-2xl border border-msd-line bg-msd-surface p-2 shadow-msd-pop";
const POP_ROW =
  "flex w-full items-center gap-[10px] rounded-[10px] px-3 py-[10px] text-left text-[14px] hover:bg-msd-soft";

function usePop() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", down);
    return () => document.removeEventListener("mousedown", down);
  }, [open]);
  return { open, setOpen, ref };
}

function ViewAs() {
  const {
    role,
    setRole,
    locations,
    setLocations,
    toggleLocation,
    advisorName,
    setAdvisorName,
    control,
    workshops,
    advisorsForWorkshop,
  } = useDemoIdentity();
  const { roleLabel, roleShort, roleRows } = useTenantTerms();
  const { open, setOpen, ref } = usePop();
  // Every role the brand defines, not just master — same list the old switcher used.
  const roleCodes = ["master", ...(roleRows.length ? roleRows.map((r) => r.code) : ROLES.filter((r) => r !== "master"))];
  const advisors = control.needsAdvisor ? advisorsForWorkshop(locations[0]) : [];
  const where =
    control.locationControl === "hidden"
      ? "All locations"
      : control.locationControl === "multi"
        ? locations.length === 1
          ? locations[0]
          : `${locations.length} locations`
        : locations[0] || "No location";
  const subtitle = control.needsAdvisor && advisorName ? `${where} · ${advisorName}` : where;
  return (
    <div className="relative" ref={ref} data-testid="demo-identity-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="demo-role-select"
        className="flex h-10 items-center gap-[10px] rounded-[10px] border border-msd-line bg-msd-surface pl-[6px] pr-[10px] text-left hover:bg-msd-soft"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-msd-soft text-msd-ink2">
          <Icon name="eye" size={16} />
        </span>
        <span className="max-[860px]:hidden">
          <span className="block max-w-[240px] truncate text-[11px] leading-[14px] text-msd-muted">Viewing as · {subtitle}</span>
          <b className="block text-[13px] font-semibold leading-[18px]">{roleLabel(role)}</b>
        </span>
        <Icon name="updown" size={16} stroke={2} color="var(--muted)" />
      </button>
      {open && (
        <div className={`${POP} top-[48px] max-h-[70vh] w-[300px] overflow-y-auto`}>
          <ViewAsSection title="Role">
            {roleCodes.map((r) => (
              <ViewAsOption
                key={r}
                testid={`demo-role-option-${r}`}
                selected={r === role}
                onClick={() => setRole(r)}
                label={roleLabel(r)}
                meta={roleShort(r)}
              />
            ))}
          </ViewAsSection>
          {control.locationControl !== "hidden" && (
            <ViewAsSection title="Locations">
              {workshops.map((w) =>
                control.locationControl === "multi" ? (
                  <ViewAsOption
                    key={w}
                    testid={`demo-location-checkbox-${w}`}
                    selected={locations.includes(w)}
                    onClick={() => toggleLocation(w)}
                    label={w}
                    checkbox
                  />
                ) : (
                  <ViewAsOption
                    key={w}
                    testid={`demo-location-option-${w}`}
                    selected={locations[0] === w}
                    onClick={() => setLocations([w])}
                    label={w}
                  />
                ),
              )}
            </ViewAsSection>
          )}
          {control.needsAdvisor && (
            <ViewAsSection title="Advisor">
              {advisors.map((a) => (
                <ViewAsOption
                  key={a.id}
                  testid={`demo-advisor-option-${a.id}`}
                  selected={advisorName === a.name}
                  onClick={() => setAdvisorName(a.name)}
                  label={a.name}
                />
              ))}
            </ViewAsSection>
          )}
        </div>
      )}
    </div>
  );
}

function ViewAsSection({ title, children }) {
  return (
    <div className="border-b border-msd-line pb-1 last:border-b-0 [&+&]:pt-1">
      <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-msd-muted">{title}</div>
      {children}
    </div>
  );
}

function ViewAsOption({ label, meta, selected, onClick, checkbox = false, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className={POP_ROW}>
      {checkbox ? (
        <span
          className={`flex h-4 w-4 flex-none items-center justify-center rounded-[4px] border ${
            selected ? "border-msd-royal bg-msd-royal text-white" : "border-msd-line bg-msd-surface"
          }`}
        >
          {selected && <Icon name="check" size={11} stroke={3} />}
        </span>
      ) : selected ? (
        <Icon name="check" size={16} stroke={2.2} color="var(--royal)" />
      ) : (
        <span className="w-4 flex-none" />
      )}
      <span className="min-w-0 flex-1 truncate font-semibold">{label}</span>
      {meta && meta !== label && (
        <span className="flex-none rounded-md bg-msd-royal-soft px-[7px] py-[2px] text-[11px] font-bold tracking-[0.02em] text-msd-royal">
          {meta}
        </span>
      )}
    </button>
  );
}

// Who is signed in, at the foot of the sidebar: their initial, name and role.
// Opens upwards to switch the theme or sign out. Folded, only the initial shows.
function AccountMenu({ open, fade, onTheme }) {
  const { me, signOut } = useAuth();
  const { roleLabel } = useTenantTerms();
  const pop = usePop();
  const name = me?.name || (me?.email || "").split("@")[0] || "Account";
  const initial = (name.trim()[0] || "?").toUpperCase();
  const dark = typeof document !== "undefined" && document.documentElement.dataset.theme === "dark";
  return (
    <div className="relative" ref={pop.ref}>
      <button
        type="button"
        onClick={() => pop.setOpen((o) => !o)}
        aria-label="Account"
        aria-expanded={pop.open}
        data-testid="msd-account"
        className="flex h-12 w-full items-center gap-3 whitespace-nowrap rounded-[10px] px-2 text-left hover:bg-msd-soft"
      >
        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-msd-btn text-[13px] font-semibold text-white">
          {initial}
        </span>
        <span className="min-w-0 flex-1" style={fade}>
          <span className="block truncate text-[13px] font-semibold leading-[18px] text-msd-ink">{name}</span>
          <span className="block truncate text-[12px] leading-4 text-msd-muted">{me?.role ? roleLabel(me.role) : me?.email}</span>
        </span>
        <span className="flex-none text-msd-faint" style={fade}>
          <Icon name="updown" size={16} stroke={2} />
        </span>
      </button>
      {pop.open && open && (
        <div className={`${POP} bottom-full left-0 mb-2 w-[240px]`}>
          <div className="truncate px-3 pb-2 pt-1 text-[12px] text-msd-muted">{me?.email}</div>
          <button
            type="button"
            onClick={() => {
              onTheme();
              pop.setOpen(false);
            }}
            className={POP_ROW}
            data-testid="msd-theme"
          >
            <Icon name={dark ? "sun" : "moon"} size={16} />
            {dark ? "Light theme" : "Dark theme"}
          </button>
          <button type="button" onClick={signOut} className={POP_ROW} data-testid="msd-signout">
            <Icon name="back" size={16} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function TopBar({ onMenu, items }) {
  const { me } = useAuth();
  const { tenant } = useTenantTerms();
  const page = items.flatMap((g) => g.items).find((n) => n.active);
  return (
    <header className="flex h-16 shrink-0 items-center gap-3 border-b border-msd-line bg-msd-surface px-8 max-[1100px]:px-6 max-[640px]:px-4">
      <button
        type="button"
        onClick={onMenu}
        aria-label="Open menu"
        className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg text-msd-ink2 hover:bg-msd-soft max-[860px]:inline-flex"
      >
        <Icon name="menu" size={20} />
      </button>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-msd-line bg-msd-surface text-msd-ink2 max-[480px]:hidden">
        {tenant?.logo_url ? (
          <img src={tenant.logo_url} alt="" className="h-full w-full object-contain p-1" />
        ) : brandInitials(tenant?.name) ? (
          <span className="text-[12px] font-bold tracking-[.02em]" data-testid="brand-initials" aria-hidden="true">
            {brandInitials(tenant?.name)}
          </span>
        ) : (
          <Icon name="logo" size={18} />
        )}
      </div>
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-[14px]">
        <span className="truncate font-semibold text-msd-ink" data-testid="brand-name">
          {tenant?.name || ""}
        </span>
        {page ? (
          <>
            <Icon name="right" size={14} stroke={2} color="var(--faint)" className="shrink-0 max-[640px]:hidden" />
            <span className="truncate text-msd-muted max-[640px]:hidden">{page.label}</span>
          </>
        ) : null}
      </nav>
      <div className="grow" />
      {me?.role === "master" && <ViewAs />}
    </header>
  );
}

// The scrolling area a page sits in, with the same padding on every page.
const PAGE_SCROLL =
  "min-h-0 min-w-0 grow overflow-auto px-8 pb-12 pt-7 max-[1100px]:px-6 max-[640px]:px-4 max-[640px]:pb-8 max-[640px]:pt-4";

export function MsdPage({ children, className = "", ...rest }) {
  return (
    <div className="flex min-h-0 grow" {...rest}>
      <div className={PAGE_SCROLL}>
        <div className={`mx-auto w-full max-w-[1400px] ${className}`}>{children}</div>
      </div>
    </div>
  );
}

export function MsdShell({ children }) {
  useRealtimeSync();
  const items = useNavItems();
  const nav = useNavigate();
  const loc = useLocation();
  const { tenant } = useTenantTerms();
  const scope = useMsdScope();
  // The phone menu (below 860px wide); wider screens use the sidebar.
  const [drawer, setDrawer] = useState(false);
  // Stays mounted for a moment after closing so it can fade out.
  const shownDrawer = useExitDelay(drawer, 180);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // Dark mode lives behind the sidebar's Theme button only, as `data-theme` on
  // <html>. It is cleared when leaving this shell so other pages are unaffected.
  useEffect(() => {
    let saved = "light";
    try {
      saved = localStorage.getItem(THEME_KEY) || "light";
    } catch (e) {
      /* storage unavailable: stay light */
    }
    const root = document.documentElement;
    root.dataset.theme = saved === "dark" ? "dark" : "light";
    return () => {
      delete root.dataset.theme;
    };
  }, []);

  const toggleTheme = () => {
    const root = document.documentElement;
    root.dataset.theme = root.dataset.theme === "dark" ? "light" : "dark";
    try {
      localStorage.setItem(THEME_KEY, root.dataset.theme);
    } catch (e) {
      /* storage unavailable: theme applies for this visit only */
    }
  };

  const show = useCallback((message) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const image = tenant?.sidebar_image_url || "";
  const mobileTabs = [
    ["tower", "Analytics", "/analytics"],
    ["desk", "Desk", "/desk"],
    ["other", "More", null],
  ];

  return (
    <ToastContext.Provider value={show}>
      <div className="msd flex h-screen max-[860px]:flex-col" data-testid="msd-app">
        <Sidebar items={items} badge={scope.escalatedCount} onTheme={toggleTheme} image={image} />
        <div className="flex min-h-0 min-w-0 grow flex-col">
          <TopBar onMenu={() => setDrawer(true)} items={items} />
          <main className="flex min-h-0 grow flex-col">{children}</main>
        </div>
        <nav
          aria-label="Sections"
          className="hidden h-14 shrink-0 grid-cols-3 border-t border-msd-line bg-msd-surface max-[860px]:grid"
        >
          {mobileTabs.map(([k, label, to]) => {
            const on = to && loc.pathname.startsWith(to);
            return (
              <button
                key={k}
                type="button"
                onClick={() => (to ? nav(to) : setDrawer(true))}
                className={`text-[13px] ${on ? "font-semibold text-msd-royal" : "text-msd-muted"}`}
              >
                {label}
              </button>
            );
          })}
        </nav>
        {shownDrawer.value && (
          <Drawer
            items={items}
            badge={scope.escalatedCount}
            leaving={shownDrawer.leaving}
            onClose={() => setDrawer(false)}
            onTheme={toggleTheme}
            image={image}
          />
        )}
        {toast && (
          <div
            role="status"
            className="fixed bottom-6 left-1/2 z-[100] flex max-w-[90vw] -translate-x-1/2 items-center gap-[10px] rounded-xl bg-[#101828] px-[18px] py-3 text-[14px] text-white shadow-msd-pop"
          >
            <Icon name="check" size={16} stroke={2.2} />
            {toast}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}
